import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Icon } from "semantic-ui-react";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";

import logoImage from "../images/512.png";
import bgImage from "../images/hauessler-application-background.webp";
import { fetchLinktree } from "../utils/requestHelper";

const PublicLinktreePage = () => {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [data, setData] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const fetchData = async (slug) => {
      const response = await fetchLinktree(slug);
      if (response?.success) setData(response.data);
    };
    fetchData(slug);
  }, [slug]);

  if (!data) return null;

  const styles = {
    content: {
      padding: "2rem 1.5rem 1rem",
      textAlign: "center",
    },

    logoWrapper: {
      display: "flex",
      justifyContent: "center",
      marginBottom: "1rem",
    },

    logo: {
      width: "30%",
      borderRadius: "50%",
      objectFit: "cover",
      border: "6px solid white",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
    },

    title: {
      fontFamily: "Alfa Slab One",
      fontSize: "clamp(2.5rem, 8vw, 4rem)",
      fontWeight: "300",
      lineHeight: "1",
      margin: "0.5rem 0 0",
      color: "rgba(255, 255, 255, 0.9)",
    },

    subtitle: {
      fontFamily: "Juri Handwriting",
      margin: "0.25rem 0 1.5rem",
      fontSize: "clamp(1.8rem, 6vw, 3rem)",
      color: "rgba(255, 255, 255, 0.9)",
    },

    links: {
      display: "flex",
      flexDirection: "column",
      gap: "0.75rem",
    },

    linkButton: {
      borderRadius: "999px",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      fontFamily: "Avenir Next Codensed Ultralight",
      fontWeight: "600",
      fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
      color: "#000000de",
      border: "none",
      padding: "1.2rem 1.5rem",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      display: "flex",
      justifyContent: "center",
      gap: "0.5rem",
      cursor: "pointer",
      width: "100%",
      lineHeight: "1.2",
    },

    footer: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      gap: "clamp(1rem, 5vw, 2rem)",
      padding: "1rem",
    },

    socialIconStyle: {
      background: "rgba(0,0,0,0.35)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      color: "#c39d63",
      boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
      cursor: "pointer",
      fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
      padding: "0.5em",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "2.2em",
      height: "2.2em",
    },
  };
  return (
    <div
      style={{
        minHeight: "100dvh",
        backgroundColor: "#eee",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: isDesktop ? "2rem" : "0",
      }}
    >
      {/* PHONE / FULLSCREEN */}
      <div
        style={{
          width: isDesktop ? "576px" : "100%",
          minHeight: "100dvh",
          borderRadius: isDesktop ? "40px" : "0",
          overflow: "hidden",
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          boxShadow: isDesktop ? "0 20px 50px rgba(0,0,0,0.3)" : "none",
          alignItems: "center",
          position: "relative",
          paddingTop: isDesktop ? "2rem" : "env(safe-area-inset-top)",
          paddingBottom: isDesktop ? "2rem" : "env(safe-area-inset-bottom)",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* CONTENT */}
        <div style={styles.content}>
          {/* LOGO */}
          <div style={styles.logoWrapper}>
            <img src={logoImage} alt="Logo" style={styles.logo} />
          </div>

          {/* TITLE */}
          <h2 style={styles.title}>{data.title}</h2>
          <p style={styles.subtitle}>{data.subtitle}</p>

          {/* LINKS */}
          <div style={styles.links}>
            {data.links.map((link) => (
              (!link.language || link.language === language) && (
                <button
                  onClick={() =>
                    window.open(link.url, link.openInNewTab ? "_blank" : "_self")
                  }
                  key={link.id}
                  fluid
                  size="large"
                  style={styles.linkButton}
                >
                  {link.icon && <Icon name={link.icon} />}
                  {link.name}
                </button>
              )
            ))}
          </div>
        </div>

        {/* FOOTER */}
        <div style={styles.footer}>
          <Icon
            link
            circular
            onClick={() =>
              window.open("https://www.instagram.com/backhaus_haeussler/")
            }
            name="instagram"
            style={styles.socialIconStyle}
          />
          <Icon
            link
            circular
            onClick={() =>
              window.open("https://www.facebook.com/BackhausHaeussler")
            }
            name="facebook f"
            style={styles.socialIconStyle}
          />
        </div>
      </div>

      {/* DESKTOP QR */}
      {isDesktop && (
        <div
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            background: "rgba(255, 255, 255, 0.9)",
            padding: "12px",
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            textAlign: "center",
          }}
        >
          <QRCodeCanvas
            value={`${window.location.origin}/l/${slug}`}
            size={120}
          />
          <div style={{ fontSize: "0.75rem", marginTop: "6px" }}>
            {i18n.t(`text-scan_qr_code`)}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicLinktreePage;
